import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { apiGet, apiPost, onSignedOut, setCachedUser } from '../lib/api';
import type { SessionUser } from '../lib/api';
import type { Profile } from '../types';

/**
 * Who is signed in.
 *
 * There is no client-side session object any more: the cookie is HttpOnly, so
 * the only way to learn who we are is to ask the server. `session` is kept as a
 * simple truthy `{ user }` wrapper because the router reads it as "is anyone
 * signed in", and that is all it ever needed.
 */
export interface AuthSession {
    user: SessionUser;
}

interface AuthState {
    /** Undefined while the first `GET /api/auth/me` is in flight. */
    session: AuthSession | null | undefined;
    user: SessionUser | null;
    /** The full own-profile row. Null for a pending account the server won't hand it to. */
    profile: Profile | null;
    isAdmin: boolean;
    isApproved: boolean;
    /** True while the session or the first profile load is still running. */
    loading: boolean;
    refreshProfile: () => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

async function fetchMe(): Promise<SessionUser | null> {
    try {
        const { user } = await apiGet<{ user: SessionUser | null }>('/api/auth/me');
        return user ?? null;
    } catch (err) {
        console.error('Could not check the session', err);
        return null;
    }
}

async function fetchProfile(): Promise<Profile | null> {
    try {
        // The Worker wraps it: { profile: { ... } }.
        const { profile } = await apiGet<{ profile: Profile | null }>('/api/profile');
        return profile ?? null;
    } catch {
        // A pending or rejected account is not allowed its own full row yet.
        // That is not an error worth showing; the pending screen covers it.
        return null;
    }
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<AuthSession | null | undefined>(undefined);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const currentUserId = useRef<string | null>(null);

    const applyUser = useCallback(async (user: SessionUser | null) => {
        setCachedUser(user);
        setSession(user ? { user } : null);
        if (!user) {
            currentUserId.current = null;
            setProfile(null);
            setProfileLoaded(true);
            return;
        }
        currentUserId.current = user.id;
        const p = await fetchProfile();
        // Ignore a stale response if the signed-in user changed mid-flight.
        if (currentUserId.current !== user.id) return;
        setProfile(p);
        setProfileLoaded(true);
    }, []);

    useEffect(() => {
        let cancelled = false;

        fetchMe().then((user) => {
            if (!cancelled) void applyUser(user);
        });

        // Any request that comes back 401 means the cookie is gone. Fall back to
        // signed-out here rather than redirecting from the HTTP layer, so the
        // router decides where the resident goes and cannot loop.
        const stop = onSignedOut(() => {
            if (cancelled) return;
            currentUserId.current = null;
            setSession(null);
            setProfile(null);
            setProfileLoaded(true);
        });

        return () => { cancelled = true; stop(); };
    }, [applyUser]);

    /**
     * Re-read who we are. Also how sign-in tells the context about the brand-new
     * cookie: the session did not exist a moment ago, so this must be able to
     * adopt a user it has never seen, not merely refresh a known one.
     */
    const refreshProfile = useCallback(async () => {
        await applyUser(await fetchMe());
    }, [applyUser]);

    const signOut = useCallback(async () => {
        try {
            await apiPost('/api/auth/signout');
        } catch {
            // Signing out locally matters more than the round trip succeeding.
        }
        setCachedUser(null);
        currentUserId.current = null;
        setProfile(null);
        setSession(null);
        setProfileLoaded(true);
    }, []);

    const value = useMemo<AuthState>(() => ({
        session,
        user: session?.user ?? null,
        profile,
        isAdmin: session?.user.role === 'admin',
        isApproved: session?.user.account_status === 'approved',
        loading: session === undefined || (!!session && !profileLoaded),
        refreshProfile,
        signOut,
    }), [session, profile, profileLoaded, refreshProfile, signOut]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
